import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import axios from "axios";
import { Send } from "lucide-react";

export const ContactForm = () => {
  const { toast } = useToast();
  const [eventType, setEventType] = useState("firmowe");
  const [budget, setBudget] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    try {
      await axios({
        method: 'POST',
        url: 'https://form.aigency.pl/api/form',
        data: {
          website: 'Wystep.pl',
          email: formData.get("email"),
          destinationEmail: 'kontakt@wystep.pl',
          fields: {
            "Imię i nazwisko": formData.get("name"),
            "Numer telefonu": formData.get("phone"),
            "Nazwa firmy": formData.get("company"),
            "Rodzaj wydarzenia": eventType,
            "Budżet orientacyjny": budget,
            "Wiadomość": formData.get("message"),
          }
        }
      });

      toast({ title: "Formularz wysłany!", description: "Skontaktujemy się z Tobą najszybciej jak to możliwe." });
      form.reset();
    } catch (error: any) {
      if (error.response?.status === 422) {
        toast({ title: "Błąd walidacji", description: error.response.data.message || "Sprawdź poprawność danych", variant: "destructive" });
      } else {
        toast({ title: "Wystąpił błąd", description: "Przepraszamy, spróbuj ponownie później", variant: "destructive" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 section-darker relative overflow-hidden" id="kontakt">
      <div className="absolute inset-0 mesh-gradient opacity-30" />
      <div className="container max-w-2xl relative z-10">
        <h2 className="text-4xl md:text-5xl font-space-grotesk font-bold text-center mb-4 text-foreground">
          Zamów <span className="text-gradient">występ</span>
        </h2>
        <p className="text-muted-foreground text-center mb-16 max-w-md mx-auto">
          Wypełnij formularz, a my odezwiemy się w ciągu 24h
        </p>

        <form onSubmit={handleSubmit} className="glass-strong rounded-2xl p-8 space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Imię i nazwisko</label>
              <Input name="name" required className="bg-secondary/50 border-border text-foreground rounded-xl focus:ring-primary" />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Email</label>
              <Input name="email" type="email" required className="bg-secondary/50 border-border text-foreground rounded-xl focus:ring-primary" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Numer telefonu</label>
              <Input name="phone" type="tel" required className="bg-secondary/50 border-border text-foreground rounded-xl focus:ring-primary" />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Nazwa firmy <span className="text-muted-foreground/60 italic">(opcjonalne)</span></label>
              <Input name="company" className="bg-secondary/50 border-border text-foreground rounded-xl focus:ring-primary" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <label className="text-sm text-muted-foreground block">Rodzaj wydarzenia</label>
              <RadioGroup defaultValue="firmowe" onValueChange={setEventType} className="flex gap-4 h-10 items-center">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="firmowe" id="firmowe" className="border-border text-primary" />
                  <Label htmlFor="firmowe" className="text-foreground">Firmowe</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="prywatne" id="prywatne" className="border-border text-primary" />
                  <Label htmlFor="prywatne" className="text-foreground">Prywatne</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-3">
              <label className="text-sm text-muted-foreground block">Budżet orientacyjny</label>
              <Select onValueChange={setBudget}>
                <SelectTrigger className="bg-secondary/50 border-border text-foreground rounded-xl focus:ring-primary h-10">
                  <SelectValue placeholder="Wybierz przedział" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="do 5000 PLN">do 5 000 PLN</SelectItem>
                  <SelectItem value="5000-10000 PLN">od 5 000 do 10 000 PLN</SelectItem>
                  <SelectItem value="10000-20000 PLN">od 10 000 do 20 000 PLN</SelectItem>
                  <SelectItem value="20000-50000 PLN">od 20 000 do 50 000 PLN</SelectItem>
                  <SelectItem value="50000-100000 PLN">od 50 000 do 100 000 PLN</SelectItem>
                  <SelectItem value="powyżej 100000 PLN">powyżej 100 000 PLN</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Wiadomość</label>
            <Textarea name="message" required className="min-h-[120px] bg-secondary/50 border-border text-foreground rounded-xl focus:ring-primary" />
          </div>

          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/80 text-primary-foreground rounded-xl py-6 text-base glow transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2"
            disabled={isSubmitting}
          >
            <Send className="w-4 h-4" />
            {isSubmitting ? "Wysyłanie..." : "Wyślij zapytanie"}
          </Button>
        </form>

        <div className="mt-12 glass-strong rounded-2xl p-8 text-center">
          <p className="text-muted-foreground text-sm mb-3 uppercase tracking-wider">Właścicielem serwisu jest</p>
          <h3 className="font-space-grotesk font-bold text-foreground text-lg mb-3">Wirtualny Biznes</h3>
          <div className="text-muted-foreground text-sm space-y-1">
            <p>ul. Księcia Witolda 49/15</p>
            <p>50-202 Wrocław</p>
            <p className="mt-2">NIP: 8961619199</p>
          </div>
        </div>
      </div>
    </section>
  );
};
